import { z } from 'zod';
import { CommercialProposalContentSchema } from './content.js';
import {
  CommercialProposalCalculationOutputSchema,
  CommercialProposalMoney2dpSchema,
} from './calculation.js';
import {
  COMMERCIAL_PROPOSAL_PUBLIC_CODE_REGEX,
  CommercialProposalStatus,
  CommercialProposalStatusSchema,
} from './status.js';

/**
 * @aiPurpose frozen snapshot captured when a proposal is published (#1194).
 *   Freezes BOTH the content inputs and the calculated outputs so the
 *   public sheet renders deterministically even if the owner later edits
 *   the draft or rates move. A draft has no snapshot.
 * @aiInvariant `content` and `calculation` are immutable post-publish;
 *   `.strict()` keeps the freeze surface in lockstep with the input
 *   contract.
 */
export const CommercialProposalSnapshotSchema = z
  .object({
    content: CommercialProposalContentSchema,
    calculation: CommercialProposalCalculationOutputSchema,
  })
  .strict();
export type CommercialProposalSnapshot = z.infer<typeof CommercialProposalSnapshotSchema>;

/**
 * @aiPurpose compact list-row projection for the owner/admin proposals
 *   index (#1194; consumed by #1198 web). Carries enough to render a row
 *   and deep-link, plus the searchable totals frozen at publish time.
 * @aiInvariant `grandTotalRub` is NULL for an unpublished DRAFT (no
 *   snapshot yet). `.strict()`. `ownerName` is the owner's display label
 *   (the owner always exists — the relation is RESTRICT); `htmlViewCount`
 *   is the LIFETIME HTML_VIEW count for the row.
 */
export const CommercialProposalListItemSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(COMMERCIAL_PROPOSAL_PUBLIC_CODE_REGEX),
    status: CommercialProposalStatusSchema,
    client: z.string(),
    productName: z.string(),
    ownerName: z.string(),
    htmlViewCount: z.number().int().nonnegative(),
    /// Frozen grand total (2dp string) once published; NULL while DRAFT.
    grandTotalRub: CommercialProposalMoney2dpSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    publishedAt: z.string().datetime().nullable(),
  })
  .strict();
export type CommercialProposalListItem = z.infer<typeof CommercialProposalListItemSchema>;

/**
 * @aiPurpose owner/admin detail projection (#1194; #1198). Carries the
 *   live editable content plus the published snapshot (when present) so
 *   the editor can show both the working draft and what the customer
 *   currently sees. This is the INTERNAL read — distinct from the public
 *   DTO, which strips internal coefficients, keys, and token fields.
 * @aiInvariant `snapshot` is non-null iff `status === PUBLISHED`.
 * @aiSecurity owner/admin-only. The recoverable public token lives on a
 *   separate reveal envelope (#1195), never on this read.
 */
export const CommercialProposalDetailSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(COMMERCIAL_PROPOSAL_PUBLIC_CODE_REGEX),
    ownerId: z.string().uuid(),
    status: CommercialProposalStatusSchema,
    content: CommercialProposalContentSchema,
    snapshot: CommercialProposalSnapshotSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    publishedAt: z.string().datetime().nullable(),
    publishedById: z.string().uuid().nullable(),
    revokedAt: z.string().datetime().nullable(),
    revokedById: z.string().uuid().nullable(),
  })
  .strict()
  .superRefine((val, ctx) => {
    // INVARIANT: snapshot is non-null iff status === PUBLISHED.
    if (val.status === CommercialProposalStatus.PUBLISHED && val.snapshot === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['snapshot'],
        message: 'snapshot must be non-null when status is PUBLISHED',
      });
    }
    if (val.status === CommercialProposalStatus.DRAFT && val.snapshot !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['snapshot'],
        message: 'snapshot must be null when status is DRAFT',
      });
    }
  });
export type CommercialProposalDetail = z.infer<typeof CommercialProposalDetailSchema>;
